// frontend/src/components/SubscriptionGate.jsx
// Shown when an authenticated user has no active subscription.
// Prompts them to subscribe via Stripe before accessing the app.

import React, { useState } from 'react';
import {
  Lock,
  TrendingUp,
  Brain,
  Activity,
  BarChart3,
  CheckCircle,
  Zap,
  LogOut,
  Settings,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AccountSettings from './AccountSettings';

const FEATURES = [
  { icon: TrendingUp, label: 'Performance Trend Charts',  desc: 'Game-by-game trends with moving averages & best-fit lines' },
  { icon: BarChart3,  label: 'Statistical Distribution',  desc: 'Normal distribution graphs & consistency scores' },
  { icon: Brain,      label: 'AI-Powered Analysis',        desc: 'Plain-English breakdowns of player form' },
  { icon: Activity,   label: 'Betting Odds Insights',      desc: 'Implied over/under odds from recent averages' },
];

const SubscriptionGate = () => {
  const { user, signOut } = useAuth();
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [settingsOpen,  setSettingsOpen]  = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email, userId: user?.id }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('[subscription-gate] checkout error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex flex-col">
      {settingsOpen && <AccountSettings onClose={() => setSettingsOpen(false)} />}

      {/* Minimal header */}
      <header className="bg-gray-900/50 backdrop-blur-xl border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <BarChart3 className="h-8 w-8 text-orange-500 mr-3" />
            <h1 className="text-2xl font-black text-white">
              Hoops<span className="text-orange-500">Analyst</span>
            </h1>
          </a>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm hidden sm:block">{user?.email}</span>
            <button
              onClick={() => setSettingsOpen(true)}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
              title="Account Settings"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:block">Account</span>
            </button>
            <button
              onClick={signOut}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Gate content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="relative overflow-hidden rounded-3xl border border-orange-500/30 bg-gradient-to-br from-gray-900/90 via-gray-900/80 to-orange-950/30 backdrop-blur-xl w-full max-w-2xl">
          {/* Decorative glows */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 p-8 sm:p-12">
            {/* Lock badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 text-orange-300 text-sm font-bold px-4 py-2 rounded-full">
                <Lock className="h-4 w-4" />
                Subscription Required
              </div>
            </div>

            {/* Headline */}
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                Welcome to <span className="text-orange-500">HoopsAnalyst</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-xl mx-auto">
                Subscribe to unlock the full analytical suite — charts, distributions, AI insights, and more for every NBA player.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-2xl mx-auto">
              {FEATURES.map(({ icon: Icon, label, desc }) => (
                <div
                  key={label}
                  className="flex items-start gap-4 bg-gray-800/40 rounded-2xl p-4 border border-gray-700/30"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-0.5">{label}</div>
                    <div className="text-gray-400 text-xs leading-relaxed">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Price */}
            <div className="text-center mb-6">
              <div className="inline-flex flex-col items-center bg-gray-800/60 border border-gray-600/40 rounded-2xl px-8 py-5">
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-5xl font-black text-white">$2.49</span>
                  <span className="text-gray-400 font-medium mb-2">/ month</span>
                </div>
                <div className="flex items-center gap-1.5 text-green-400 text-sm font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Cancel anytime
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 max-w-md mx-auto p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-300 text-sm text-center">
                {error}
              </div>
            )}

            {/* CTA */}
            <div className="flex justify-center">
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-orange-500/30 hover:shadow-2xl hover:-translate-y-0.5 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Redirecting to Checkout…
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5" />
                    Get Full Access — $2.49/mo
                  </>
                )}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-500">
              Secured by Stripe · SSL encrypted · Cancel anytime from your account
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGate;
